import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { sendDirectWhatsApp, sendDirectSMS, formatPhoneNumberForDirect } from '../utils/directMessaging';

const AdminUsers = ({ showMessage, userRole }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Direct Notification state
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationChannel, setNotificationChannel] = useState('whatsapp');
    const [notificationTitle, setNotificationTitle] = useState('QwickMed Pharmacy');
    const [selectedUserForNotification, setSelectedUserForNotification] = useState(null);
    const [isSendingPush, setIsSendingPush] = useState(false);

    // Direct Bulk Broadcast state
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkMessage, setBulkMessage] = useState('Hi {name}, order medicines quickly with flat discounts & fast home delivery from QwickMed! Visit: www.qwickmed.com');
    const [bulkChannel, setBulkChannel] = useState('whatsapp');
    const [bulkTitle, setBulkTitle] = useState('Special Announcement from QwickMed');
    const [bulkTargetRole, setBulkTargetRole] = useState('customers');
    const [bulkQueueIndex, setBulkQueueIndex] = useState(0);
    const [isQueueActive, setIsQueueActive] = useState(false);
    const [bulkSelectionMode, setBulkSelectionMode] = useState('all'); // 'all' or 'chosen'
    const [modalUserFilter, setModalUserFilter] = useState('');

    // Customer Selection & Search for Targeted / Bulk Messaging
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [userSearchTerm, setUserSearchTerm] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [step, setStep] = useState(1); // 1: Mobile, 2: OTP, 3: Details
    const [newCustomer, setNewCustomer] = useState({ mobile: '', otp: '', name: '', address: '', pincode: '' });
    const [isOtpBypassed, setIsOtpBypassed] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [idToken, setIdToken] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Edit User state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', address: '', pincode: '', mobile: '' });

    // Tab state
    const availableTabs = userRole === 'super_admin' ? ['Customers', 'Drivers', 'Admins'] : ['Customers', 'Drivers'];
    const [activeTab, setActiveTab] = useState('Customers');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [adminsList, setAdminsList] = useState([]);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const data = await callApi('/admin/users');
            if (data && Array.isArray(data)) {
                data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            }
            setUsers(data);
        } catch (error) {
            console.error("Failed to fetch users", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdmins = async () => {
        if (userRole === 'super_admin') {
            try {
                const data = await callApi('/admin/admins');
                if (data.success) {
                    setAdminsList(data.admins);
                }
            } catch (error) {
                console.error("Failed to fetch admins", error);
            }
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchAdmins();
    }, [userRole]);

    const updateUserRole = async (userId, newRole) => {
        try {
            await callApi(`/admin/users/${userId}/role`, 'PUT', { role: newRole });
            showMessage(`User role updated to ${newRole}.`, 'success');
            fetchUsers();
        } catch (error) {
            showMessage('Failed to update user role.', 'error');
        }
    };

    const assignDriverToAdmin = async (driverId, adminId) => {
        try {
            await callApi(`/admin/users/${driverId}/assign-admin`, 'PUT', { assignedAdmin: adminId });
            showMessage('Driver assigned to store successfully.', 'success');
            fetchUsers();
        } catch (error) {
            showMessage('Failed to assign driver.', 'error');
        }
    };

    const handleSendNotification = async () => {
        if (!notificationMessage.trim()) {
            showMessage('Please enter a message.', 'error');
            return;
        }
        if (!selectedUserForNotification) {
            showMessage('Please select a recipient.', 'error');
            return;
        }
        
        const recipientName = selectedUserForNotification.name || 'Customer';
        const personalizedMsg = notificationMessage.replace(/{name}/g, recipientName);

        if (notificationChannel === 'push') {
            setIsSendingPush(true);
            try {
                await callApi('/admin/send-notification', 'POST', {
                    userId: selectedUserForNotification._id,
                    phone: selectedUserForNotification.mobile,
                    title: notificationTitle || 'QwickMed Pharmacy',
                    body: personalizedMsg,
                });
                showMessage(`Push notification sent to ${recipientName}!`, 'success');
                setIsNotificationModalOpen(false);
            } catch (error) {
                showMessage(error.message || 'Failed to send push notification.', 'error');
            } finally {
                setIsSendingPush(false);
            }
            return;
        }

        if (!selectedUserForNotification.mobile) {
            showMessage('User does not have a valid mobile number.', 'error');
            return;
        }

        try {
            if (notificationChannel === 'whatsapp') {
                sendDirectWhatsApp(selectedUserForNotification.mobile, personalizedMsg);
                showMessage(`Opened WhatsApp for ${recipientName}!`, 'success');
            } else {
                sendDirectSMS(selectedUserForNotification.mobile, personalizedMsg);
                showMessage(`Opened SMS app for ${recipientName}!`, 'success');
            }
            setIsNotificationModalOpen(false);
        } catch (error) {
            showMessage(error.message || 'Failed to open messaging app.', 'error');
        }
    };

    const handleBroadcastPush = async () => {
        const recipients = getBulkRecipients();
        if (recipients.length === 0) {
            showMessage('No recipients found.', 'error');
            return;
        }
        if (!bulkMessage.trim()) {
            showMessage('Please enter a message.', 'error');
            return;
        }
        setIsSendingPush(true);
        try {
            const userIds = recipients.map(u => u._id);
            await callApi('/admin/send-bulk-notification', 'POST', {
                userIds,
                title: bulkTitle || 'QwickMed Pharmacy',
                body: bulkMessage,
            });
            showMessage(`Push broadcast sent to ${recipients.length} recipients!`, 'success');
            setIsBulkModalOpen(false);
            setIsQueueActive(false);
            setBulkQueueIndex(0);
        } catch (error) {
            showMessage(error.message || 'Failed to send bulk push notification.', 'error');
        } finally {
            setIsSendingPush(false);
        }
    };

    const handleQuickDirectWhatsApp = (user) => {
        if (!user.mobile) {
            showMessage('User does not have a valid mobile number.', 'error');
            return;
        }
        setSelectedUserForNotification(user);
        setNotificationChannel('whatsapp');
        if (!notificationMessage) {
            setNotificationMessage(`Hi {name}, thank you for choosing QwickMed! How can we assist you with your prescriptions today?`);
        }
        setIsNotificationModalOpen(true);
    };

    const handleQuickDirectSMS = (user) => {
        if (!user.mobile) {
            showMessage('User does not have a valid mobile number.', 'error');
            return;
        }
        setSelectedUserForNotification(user);
        setNotificationChannel('sms');
        if (!notificationMessage) {
            setNotificationMessage(`Hi {name}, your medicine order and prescriptions are available at QwickMed: www.qwickmed.com`);
        }
        setIsNotificationModalOpen(true);
    };

    const getBulkRecipients = () => {
        return users.filter(u => {
            if (!u.mobile) return false;
            if (bulkSelectionMode === 'chosen') {
                return selectedUserIds.has(u._id);
            }
            const isUserAdmin = u.role === 'super_admin' || u.role === 'admin' || u.isAdmin;
            if (bulkTargetRole === 'customers') return !isUserAdmin && u.role !== 'driver';
            if (bulkTargetRole === 'driver') return u.role === 'driver';
            return true;
        });
    };

    const handleToggleSelectUser = (userId) => {
        const next = new Set(selectedUserIds);
        if (next.has(userId)) {
            next.delete(userId);
        } else {
            next.add(userId);
        }
        setSelectedUserIds(next);
    };

    const handleSelectAllInView = () => {
        const validUsers = filteredUsers.filter(u => u.mobile);
        const allSelected = validUsers.length > 0 && validUsers.every(u => selectedUserIds.has(u._id));
        const next = new Set(selectedUserIds);
        if (allSelected) {
            validUsers.forEach(u => next.delete(u._id));
        } else {
            validUsers.forEach(u => next.add(u._id));
        }
        setSelectedUserIds(next);
    };

    const handleClearSelection = () => {
        setSelectedUserIds(new Set());
    };

    const handleSendCurrentInQueue = () => {
        const recipients = getBulkRecipients();
        if (bulkQueueIndex >= recipients.length) {
            showMessage('Broadcast queue completed!', 'success');
            setIsQueueActive(false);
            setBulkQueueIndex(0);
            return;
        }

        const currentRecipient = recipients[bulkQueueIndex];
        const personalizedMsg = bulkMessage.replace(/{name}/g, currentRecipient.name || 'Customer');

        try {
            if (bulkChannel === 'whatsapp') {
                sendDirectWhatsApp(currentRecipient.mobile, personalizedMsg);
            } else {
                sendDirectSMS(currentRecipient.mobile, personalizedMsg);
            }

            if (bulkQueueIndex + 1 < recipients.length) {
                setBulkQueueIndex(prev => prev + 1);
            } else {
                showMessage('All broadcast messages opened successfully!', 'success');
                setIsQueueActive(false);
                setBulkQueueIndex(0);
                setIsBulkModalOpen(false);
            }
        } catch (error) {
            showMessage(error.message || 'Failed to dispatch message.', 'error');
        }
    };

    const handleCopyAllNumbers = () => {
        const recipients = getBulkRecipients();
        const numbers = recipients.map(u => formatPhoneNumberForDirect(u.mobile)).filter(Boolean).join(', ');
        navigator.clipboard.writeText(numbers);
        showMessage(`Copied ${recipients.length} phone numbers to clipboard!`, 'success');
    };

    const handleSendOtp = async () => {
        if (!newCustomer.mobile) {
            showMessage('Please enter a mobile number.', 'error');
            return;
        }
        let mobile = newCustomer.mobile;
        if (!mobile.startsWith('+91')) {
            mobile = '+91' + mobile;
        }

        setIsProcessing(true);
        // Safely clear any dangling recaptcha instances
        if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch(e) {}
            window.recaptchaVerifier = null;
        }
        
        const oldContainer = document.getElementById('recaptcha-container-admin');
        if (oldContainer) oldContainer.remove();

        const containerId = 'recaptcha-container-admin';
        const recaptchaDiv = document.createElement('div');
        recaptchaDiv.id = containerId;
        document.body.appendChild(recaptchaDiv);

        try {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
                size: 'invisible'
            });

            const confirmation = await signInWithPhoneNumber(auth, mobile, window.recaptchaVerifier);
            setConfirmationResult(confirmation);
            setStep(2);
            showMessage('OTP sent to customer successfully.', 'success');
        } catch (error) {
            console.error('Error sending OTP:', error);
            showMessage('Failed to send OTP. Ensure number is valid.', 'error');
            if (window.recaptchaVerifier) {
                try { window.recaptchaVerifier.clear(); } catch(e) {}
                window.recaptchaVerifier = null;
            }
            recaptchaDiv.remove();
        } finally {
            setIsProcessing(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!newCustomer.otp || newCustomer.otp.length < 6) {
            showMessage('Enter a valid 6-digit OTP.', 'error');
            return;
        }
        setIsProcessing(true);
        try {
            const result = await confirmationResult.confirm(newCustomer.otp);
            const user = result.user;
            const token = await user.getIdToken();
            setIdToken(token);
            setStep(3);
            showMessage('OTP Verified!', 'success');
        } catch (error) {
            showMessage('Invalid OTP.', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCreateCustomer = async () => {
        if (!newCustomer.name) {
            showMessage('Please enter the customer name.', 'error');
            return;
        }
        setIsProcessing(true);
        try {
            let mobile = newCustomer.mobile;
            if (!mobile.startsWith('+91')) {
                mobile = '+91' + mobile;
            }

            if (isOtpBypassed) {
                // Call the admin-only create customer endpoint without Firebase
                await callApi('/admin/users/create-customer', 'POST', {
                    name: newCustomer.name,
                    mobile,
                    address: newCustomer.address,
                    pincode: newCustomer.pincode
                });
                
                showMessage('Customer created successfully without OTP!', 'success');
            } else {
                // Generate random 4-digit MPIN
                const mpin = String(Math.floor(1000 + Math.random() * 9000));
                
                await callApi('/users/complete-registration', 'POST', {
                    idToken,
                    name: newCustomer.name,
                    mpin,
                    mobile,
                    address: newCustomer.address,
                    pincode: newCustomer.pincode
                });
                
                showMessage('Customer created successfully!', 'success');
                // Important: Sign out of Firebase so admin isn't logged in as the new user in Firebase context
                await signOut(auth);
            }
            
            // Success reset
            setShowModal(false);
            setStep(1);
            setIsOtpBypassed(false);
            setNewCustomer({ mobile: '', otp: '', name: '', address: '', pincode: '' });
            fetchUsers();
        } catch (error) {
            showMessage(error.message || 'Failed to create customer. They might already exist.', 'error');
            if (!isOtpBypassed) {
                await signOut(auth);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setStep(1);
        setIsOtpBypassed(false);
        setNewCustomer({ mobile: '', otp: '', name: '', address: '', pincode: '' });
        if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch(e) {}
            window.recaptchaVerifier = null;
        }
        const oldContainer = document.getElementById('recaptcha-container-admin');
        if (oldContainer) oldContainer.remove();
    };

    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setEditForm({
            name: user.name || '',
            address: user.address || '',
            pincode: user.pincode || '',
            mobile: user.mobile ? user.mobile.replace(/^\+91/, '') : ''
        });
        setIsEditModalOpen(true);
    };

    const handleSaveUserDetails = async () => {
        if (!editForm.name.trim()) {
            showMessage('Customer name cannot be empty.', 'error');
            return;
        }
        setIsProcessing(true);
        try {
            let formattedMobile = editForm.mobile.trim();
            if (formattedMobile && !formattedMobile.startsWith('+91')) {
                formattedMobile = '+91' + formattedMobile;
            }

            await callApi(`/admin/users/${editingUser._id}`, 'PUT', {
                name: editForm.name.trim(),
                address: editForm.address.trim(),
                pincode: editForm.pincode.trim(),
                mobile: formattedMobile || undefined
            });

            showMessage('Customer details updated successfully!', 'success');
            setIsEditModalOpen(false);
            setEditingUser(null);
            fetchUsers();
        } catch (error) {
            showMessage(error.message || 'Failed to update customer details.', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-400 font-medium">Loading customers...</p>
            </div>
        );
    }

    const filteredUsers = users.filter(u => {
        const isUserAdmin = u.role === 'super_admin' || u.role === 'admin' || u.isAdmin;
        let roleMatches = false;
        if (activeTab === 'Admins') roleMatches = isUserAdmin;
        else if (activeTab === 'Drivers') roleMatches = u.role === 'driver';
        else roleMatches = !isUserAdmin && u.role !== 'driver';

        if (!roleMatches) return false;

        if (userSearchTerm.trim()) {
            const query = userSearchTerm.toLowerCase();
            const nameMatch = (u.name || '').toLowerCase().includes(query);
            const mobileMatch = (u.mobile || '').includes(query);
            const addressMatch = (u.address || '').toLowerCase().includes(query);
            const pincodeMatch = (u.pincode || '').includes(query);
            return nameMatch || mobileMatch || addressMatch || pincodeMatch;
        }

        return true;
    });

    return (
        <div className="space-y-6 animate-fade-in flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
                <div>
                    <h2 className="text-2xl font-extrabold text-slate-800">User Management</h2>
                    <p className="text-sm font-medium text-slate-500 mt-0.5">{users.length} registered accounts</p>
                </div>
                <div className="flex flex-wrap gap-2.5">
                    <button 
                        onClick={() => {
                            setBulkSelectionMode(selectedUserIds.size > 0 ? 'chosen' : 'all');
                            setIsBulkModalOpen(true);
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-3.5 rounded-xl transition-all shadow-md shadow-emerald-500/20 flex items-center gap-2 text-sm"
                        title="Send broadcast in bulk to all or chosen customers"
                    >
                        <i className="fas fa-bullhorn"></i> Broadcast
                    </button>
                    <button 
                        onClick={() => setShowModal(true)}
                        className="bg-slate-800 hover:bg-slate-900 text-white font-bold py-2 px-3.5 rounded-xl transition-all shadow-md shadow-slate-900/10 flex items-center gap-2 text-sm"
                    >
                        <i className="fas fa-user-plus"></i> Add Customer
                    </button>
                </div>
            </div>
            
            {/* Tabs & Search Filter */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 border-b border-slate-200 pb-3">
                <div className="flex gap-4">
                    {availableTabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                                setSelectedUserIds(new Set());
                                setCurrentPage(1);
                            }}
                            className={`pb-2 px-1 font-bold text-sm transition-all border-b-2 ${
                                activeTab === tab 
                                    ? 'border-sky-500 text-sky-600' 
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {tab}
                            <span className="ml-2 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-extrabold">
                                {users.filter(u => {
                                    const isUserAdmin = u.role === 'super_admin' || u.role === 'admin' || u.isAdmin;
                                    if (tab === 'Admins') return isUserAdmin;
                                    if (tab === 'Drivers') return u.role === 'driver';
                                    return !isUserAdmin && u.role !== 'driver';
                                }).length}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Customer Search Bar */}
                <div className="relative w-full md:w-80">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <i className="fas fa-search text-xs"></i>
                    </span>
                    <input
                        type="text"
                        value={userSearchTerm}
                        onChange={(e) => {
                            setUserSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search customer name, mobile, pincode..."
                        className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-all shadow-sm"
                    />
                    {userSearchTerm && (
                        <button
                            onClick={() => setUserSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
                        >
                            <i className="fas fa-times-circle text-xs"></i>
                        </button>
                    )}
                </div>
            </div>

            {/* Selection Banner when Customers are Picked */}
            {selectedUserIds.size > 0 && (
                <div className="bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-xl flex flex-wrap items-center justify-between gap-3 animate-fade-in border border-slate-800">
                    <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center font-extrabold text-sm border border-sky-500/30">
                            {selectedUserIds.size}
                        </span>
                        <div>
                            <p className="text-xs font-extrabold text-white">
                                {selectedUserIds.size} {selectedUserIds.size === 1 ? 'customer' : 'customers'} chosen for message
                            </p>
                            <p className="text-[11px] text-slate-400">
                                Send direct WhatsApp or SMS to these chosen names without Twilio
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                setBulkSelectionMode('chosen');
                                setBulkChannel('whatsapp');
                                setIsBulkModalOpen(true);
                            }}
                            className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 shadow-md shadow-emerald-500/20"
                        >
                            <i className="fab fa-whatsapp"></i> WhatsApp ({selectedUserIds.size})
                        </button>
                        <button
                            onClick={() => {
                                setBulkSelectionMode('chosen');
                                setBulkChannel('sms');
                                setIsBulkModalOpen(true);
                            }}
                            className="px-3.5 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 shadow-md shadow-sky-500/20"
                        >
                            <i className="fas fa-comment-dots"></i> SMS ({selectedUserIds.size})
                        </button>
                        <button
                            onClick={handleClearSelection}
                            className="px-2.5 py-1.5 text-slate-400 hover:text-white text-xs font-bold transition-all ml-1"
                            title="Clear selection"
                        >
                            <i className="fas fa-times mr-1"></i> Clear
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden flex-grow flex flex-col">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[760px]">
                        <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                                <th className="pl-5 pr-2 py-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={filteredUsers.length > 0 && filteredUsers.filter(u => u.mobile).every(u => selectedUserIds.has(u._id))}
                                        onChange={handleSelectAllInView}
                                        className="w-4 h-4 text-sky-600 rounded border-slate-300 focus:ring-sky-500 cursor-pointer"
                                        title="Select/Deselect all in view"
                                    />
                                </th>
                                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Customer Details</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Mobile Number</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Joined Date</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(user => {
                                const initials = (user.name || 'User').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                                
                                return (
                                    <tr key={user._id} className={`hover:bg-slate-50/50 transition-colors group ${selectedUserIds.has(user._id) ? 'bg-sky-50/30' : ''}`}>
                                        <td className="pl-5 pr-2 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.has(user._id)}
                                                onChange={() => handleToggleSelectUser(user._id)}
                                                disabled={!user.mobile}
                                                className="w-4 h-4 text-sky-600 rounded border-slate-300 focus:ring-sky-500 cursor-pointer disabled:opacity-30"
                                                title={user.mobile ? "Choose this customer for bulk message" : "No mobile number"}
                                            />
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-12 rounded-xl flex flex-shrink-0 items-center justify-center font-extrabold text-white shadow-inner ${
                                                    (user.role === 'admin' || user.role === 'super_admin' || user.isAdmin) 
                                                        ? 'bg-gradient-to-br from-purple-400 to-indigo-500'
                                                        : 'bg-gradient-to-br from-sky-400 to-blue-500'
                                                }`}>
                                                    {initials}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-800">{user.name || 'Anonymous User'}</p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                                        <span className="text-[11px] text-slate-500 font-mono">ID: {user._id.substring(user._id.length - 8)}</span>
                                                        {user.pincode && (
                                                            <span className="inline-flex items-center text-[10px] font-bold bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded border border-sky-100">
                                                                <i className="fas fa-map-pin mr-1 text-[9px]"></i> {user.pincode}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {user.address && (
                                                        <p className="text-[11px] text-slate-400 truncate max-w-[220px] mt-0.5" title={user.address}>
                                                            {user.address}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1 items-start">
                                                <span className="font-medium text-slate-600 font-mono">{user.mobile || 'N/A'}</span>
                                                {user.fcmTokens && user.fcmTokens.length > 0 && (
                                                    <span 
                                                        className="inline-flex items-center text-[9px] font-bold bg-violet-50 text-violet-600 border border-violet-200 px-1.5 py-0.5 rounded shadow-sm"
                                                        title={`Push Enabled on ${user.fcmTokens.some(t => t.length < 152 || t.includes('Exponent') || t.includes(':')) ? 'App / Device' : 'Web Browser'} (${user.fcmTokens.length} device${user.fcmTokens.length > 1 ? 's' : ''})`}
                                                    >
                                                        <i className="fas fa-bell mr-1"></i> 
                                                        {user.fcmTokens.some(t => t.length < 152 || t.includes('Exponent') || t.includes(':')) ? 'App' : 'Web'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {(user.role === 'super_admin') ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-amber-50 text-amber-700">
                                                    <i className="fas fa-crown text-[10px]"></i> Super Admin
                                                </span>
                                            ) : (user.role === 'admin' || user.isAdmin) ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-purple-50 text-purple-700">
                                                    <i className="fas fa-shield-alt text-[10px]"></i> Admin
                                                </span>
                                            ) : (user.role === 'driver') ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-green-50 text-green-700">
                                                    <i className="fas fa-motorcycle text-[10px]"></i> Driver
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-slate-100 text-slate-600">
                                                    <i className="fas fa-user text-[10px]"></i> Customer
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-medium text-slate-500">
                                                {new Date(user.createdAt).toLocaleDateString('en-GB')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex gap-2 justify-end items-center">
                                                <button
                                                    onClick={() => handleOpenEditModal(user)}
                                                    className="bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2.5 py-1.5 rounded-lg font-bold text-[11px] uppercase tracking-wider transition-all shadow-sm flex items-center gap-1.5"
                                                    title="Edit Name, Address, Pincode"
                                                >
                                                    <i className="fas fa-edit text-xs"></i> Edit
                                                </button>
                                                {userRole === 'super_admin' && user.role === 'driver' && (
                                                    <select
                                                        value={user.assignedAdmin || ''}
                                                        onChange={(e) => assignDriverToAdmin(user._id, e.target.value)}
                                                        className="text-[11px] font-bold tracking-wider px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 focus:border-emerald-500 focus:ring-0 outline-none transition-all cursor-pointer"
                                                        title="Assign to Store/Admin"
                                                    >
                                                        <option value="">No Store Assigned</option>
                                                        {adminsList.map(admin => (
                                                            <option key={admin._id} value={admin._id}>
                                                                {admin.name || admin.mobile}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                {userRole === 'super_admin' || userRole === 'admin' ? (
                                                    user.role !== 'super_admin' ? (
                                                        <select 
                                                            value={user.role || 'user'}
                                                            onChange={(e) => updateUserRole(user._id, e.target.value)}
                                                            className="text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-sky-500 focus:ring-0 outline-none transition-all cursor-pointer"
                                                        >
                                                            <option value="user">Customer</option>
                                                            <option value="driver">Driver</option>
                                                            {userRole === 'super_admin' && <option value="admin">Admin</option>}
                                                        </select>
                                                    ) : (
                                                        <span className="text-xs text-slate-400 italic">Cannot modify Super Admin</span>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-slate-400 italic" title="Insufficient permissions"><i className="fas fa-lock mr-1"></i> Locked</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredUsers.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium">
                                        No users found in this category.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {Math.ceil(filteredUsers.length / itemsPerPage) > 1 && (
                    <div className="flex justify-between items-center p-4 border-t border-slate-100 bg-slate-50">
                        <button 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                            disabled={currentPage === 1}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Previous
                        </button>
                        <span className="text-xs font-bold text-slate-500">
                            Page {currentPage} of {Math.ceil(filteredUsers.length / itemsPerPage)}
                        </span>
                        <button 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredUsers.length / itemsPerPage)))} 
                            disabled={currentPage === Math.ceil(filteredUsers.length / itemsPerPage)}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Add Customer Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-2 sm:p-4">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                        <div className="bg-slate-50 px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-extrabold text-slate-800">Add New Customer</h3>
                            <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="p-4 sm:p-6 overflow-y-auto">
                            {step === 1 && (
                                <div className="space-y-4 animate-fade-in">
                                    <p className="text-sm text-slate-600 mb-4">Enter the customer's mobile number to send them an OTP.</p>
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Mobile Number</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-slate-500 font-bold">+91</span>
                                            </div>
                                            <input 
                                                type="text" 
                                                value={newCustomer.mobile}
                                                onChange={e => setNewCustomer({...newCustomer, mobile: e.target.value})}
                                                placeholder="Enter 10-digit number"
                                                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <button 
                                            onClick={handleSendOtp}
                                            disabled={isProcessing}
                                            className="w-full py-3 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-xl font-bold shadow-md shadow-sky-500/20 transition-all flex justify-center items-center gap-2"
                                        >
                                            {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Sending...</> : 'Send OTP'}
                                        </button>
                                        <button 
                                            onClick={() => {
                                                if (!newCustomer.mobile) {
                                                    showMessage('Please enter a mobile number first.', 'error');
                                                    return;
                                                }
                                                setIsOtpBypassed(true);
                                                setStep(3);
                                            }}
                                            disabled={isProcessing}
                                            className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-xl font-bold shadow-md shadow-amber-500/20 transition-all flex justify-center items-center gap-2"
                                        >
                                            <i className="fas fa-forward"></i> Skip OTP (Admin Only)
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-4 animate-fade-in">
                                    <p className="text-sm text-slate-600 mb-4">Ask the customer for the 6-digit OTP sent to their phone.</p>
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">OTP Code</label>
                                        <input 
                                            type="text" 
                                            value={newCustomer.otp}
                                            onChange={e => setNewCustomer({...newCustomer, otp: e.target.value})}
                                            placeholder="Enter 6-digit OTP"
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold text-center tracking-[0.5em]"
                                            maxLength="6"
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <button 
                                            onClick={() => setStep(1)}
                                            className="w-1/3 py-3 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold transition-all"
                                        >
                                            Back
                                        </button>
                                        <button 
                                            onClick={handleVerifyOtp}
                                            disabled={isProcessing}
                                            className="w-2/3 py-3 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-xl font-bold shadow-md shadow-sky-500/20 transition-all flex justify-center items-center gap-2"
                                        >
                                            {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Verifying...</> : 'Verify OTP'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div className="space-y-4 animate-fade-in">
                                    <p className="text-sm text-slate-600 mb-4">{isOtpBypassed ? "OTP Bypassed." : "OTP verified!"} Now enter the customer's details.</p>
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Customer Full Name</label>
                                        <input 
                                            type="text" 
                                            value={newCustomer.name}
                                            onChange={e => setNewCustomer({...newCustomer, name: e.target.value})}
                                            placeholder="e.g. John Doe"
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold mb-4"
                                        />
                                        <div className="mb-4">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Delivery Pincode (Optional)</label>
                                            <input 
                                                type="text" 
                                                value={newCustomer.pincode}
                                                onChange={e => setNewCustomer({...newCustomer, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                                                placeholder="e.g. 695125"
                                                maxLength="6"
                                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold"
                                            />
                                        </div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Delivery Address (Optional)</label>
                                        <textarea 
                                            value={newCustomer.address}
                                            onChange={e => setNewCustomer({...newCustomer, address: e.target.value})}
                                            placeholder="Enter complete address"
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold resize-none h-24 mb-4"
                                        ></textarea>
                                    </div>
                                    <button 
                                        onClick={handleCreateCustomer}
                                        disabled={isProcessing}
                                        className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl font-bold shadow-md shadow-emerald-500/20 transition-all flex justify-center items-center gap-2"
                                    >
                                        {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Creating...</> : 'Create Customer'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Direct WhatsApp, SMS & Push Notification Modal */}
            {isNotificationModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-2 sm:p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden relative border border-slate-100">
                        <div className="bg-slate-50 px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-inner ${
                                    notificationChannel === 'whatsapp' ? 'bg-emerald-100 text-emerald-600' : notificationChannel === 'push' ? 'bg-violet-100 text-violet-600' : 'bg-sky-100 text-sky-600'
                                }`}>
                                    <i className={notificationChannel === 'whatsapp' ? 'fab fa-whatsapp' : notificationChannel === 'push' ? 'fas fa-bell' : 'fas fa-comment-dots'}></i>
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">
                                        {notificationChannel === 'whatsapp' ? 'Send WhatsApp Message' : notificationChannel === 'push' ? 'Send App Notification' : 'Send SMS Message'}
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium truncate max-w-[200px] sm:max-w-xs">
                                        Recipient: <span className="font-bold text-slate-700">{selectedUserForNotification?.name || 'Customer'}</span> ({selectedUserForNotification?.mobile})
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsNotificationModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors shrink-0"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
                                {/* Choose Customer Name Dropdown */}
                                <div>
                                    <div className="flex justify-between items-center mb-1.5">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                                            Choose Customer Name
                                        </label>
                                        <span className="text-[11px] font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-100">
                                            {users.filter(u => u.mobile).length} Available
                                        </span>
                                    </div>
                                    <select
                                        value={selectedUserForNotification?._id || ''}
                                        onChange={(e) => {
                                            const found = users.find(u => u._id === e.target.value);
                                            if (found) setSelectedUserForNotification(found);
                                        }}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none font-semibold text-xs cursor-pointer shadow-sm transition-all"
                                    >
                                        <option value="" disabled>-- Select Customer by Name --</option>
                                        {users.filter(u => u.mobile).map(u => (
                                            <option key={u._id} value={u._id}>
                                                {u.name || 'Anonymous Customer'} — {u.mobile} {u.pincode ? `(PIN: ${u.pincode})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedUserForNotification && (
                                        <div className="flex items-center justify-between text-xs text-slate-500 font-medium mt-1.5 px-2 py-1 bg-slate-50 rounded-lg border border-slate-100">
                                            <span><i className="fas fa-phone-alt text-[10px] text-slate-400 mr-1"></i> <strong className="text-slate-700 font-mono">{selectedUserForNotification.mobile}</strong></span>
                                            {selectedUserForNotification.pincode && (
                                                <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-bold">
                                                    PIN: {selectedUserForNotification.pincode}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Choose Channel</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setNotificationChannel('whatsapp')}
                                            className={`py-3 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                notificationChannel === 'whatsapp'
                                                    ? 'border-emerald-500 bg-emerald-50/70 text-emerald-700 shadow-sm'
                                                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                            }`}
                                        >
                                            <i className="fab fa-whatsapp text-emerald-600 text-lg"></i> WhatsApp
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setNotificationChannel('sms')}
                                            className={`py-3 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                notificationChannel === 'sms'
                                                    ? 'border-sky-500 bg-sky-50/70 text-sky-700 shadow-sm'
                                                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                            }`}
                                        >
                                            <i className="fas fa-comment-dots text-sky-600 text-base"></i> Native SMS
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setNotificationChannel('push')}
                                            className={`py-3 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                notificationChannel === 'push'
                                                    ? 'border-violet-500 bg-violet-50/70 text-violet-700 shadow-sm'
                                                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                            }`}
                                        >
                                            <i className="fas fa-bell text-violet-600 text-base"></i> App Push
                                        </button>
                                    </div>
                                    {notificationChannel === 'push' ? (
                                        <p className="text-[11px] text-violet-500 mt-1.5 flex items-center gap-1">
                                            <i className="fas fa-bell text-violet-400"></i> Sends a Firebase push notification to the customer's app.
                                        </p>
                                    ) : (
                                        <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                                            <i className="fas fa-info-circle text-slate-400"></i> Opens directly in your browser / app without Twilio charges.
                                        </p>
                                    )}
                                    {notificationChannel === 'push' && (
                                        <div className="mt-3">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Notification Title</label>
                                            <input
                                                type="text"
                                                value={notificationTitle}
                                                onChange={(e) => setNotificationTitle(e.target.value)}
                                                placeholder="e.g. QwickMed Pharmacy"
                                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-violet-400 rounded-xl outline-none transition-all font-semibold text-sm"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Quick Templates</label>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {[
                                            { label: 'Refill Reminder', text: 'Hi {name}, it is time to refill your regular medicines! Order online with fast delivery from QwickMed: www.qwickmed.com' },
                                            { label: 'Order Ready', text: 'Hi {name}, your medicine order from QwickMed Pharmacy is ready and being prepared for delivery!' },
                                            { label: 'Special 15% Off', text: 'Hi {name}! Celebrate wellness with flat 15% off all healthcare and FMCG products at QwickMed. Use code HEALTH15.' },
                                            { label: 'Thank You', text: 'Hi {name}, thank you for choosing QwickMed for your healthcare needs! We are always here to assist you.' }
                                        ].map((tpl, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => setNotificationMessage(tpl.text)}
                                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors"
                                            >
                                                {tpl.label}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Message Content</label>
                                    <textarea 
                                        value={notificationMessage}
                                        onChange={(e) => setNotificationMessage(e.target.value)}
                                        placeholder="Type your message here... Use {name} to insert customer name"
                                        rows="4"
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-medium resize-none text-sm"
                                    ></textarea>
                                </div>

                                {notificationMessage.trim() && selectedUserForNotification && (
                                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Live Preview (Recipient View):</span>
                                        <p className="text-xs text-slate-700 font-medium whitespace-pre-wrap">
                                            {notificationMessage.replace(/{name}/g, selectedUserForNotification.name || 'Customer')}
                                        </p>
                                    </div>
                                )}

                                <div className="flex gap-3 pt-3">
                                    <button 
                                        type="button"
                                        onClick={() => setIsNotificationModalOpen(false)}
                                        className="w-1/3 py-3 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleSendNotification}
                                        disabled={!notificationMessage.trim() || isSendingPush}
                                        className={`w-2/3 py-3 text-white rounded-xl font-bold shadow-md transition-all flex justify-center items-center gap-2 ${
                                            notificationChannel === 'whatsapp'
                                                ? 'bg-[#25D366] hover:bg-[#128C7E] shadow-emerald-500/20'
                                                : notificationChannel === 'push'
                                                ? 'bg-violet-600 hover:bg-violet-700 shadow-violet-500/20'
                                                : 'bg-sky-500 hover:bg-sky-600 shadow-sky-500/20'
                                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                                    >
                                        {isSendingPush && notificationChannel === 'push' ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Sending...</>
                                        ) : (
                                            <><i className={notificationChannel === 'whatsapp' ? 'fab fa-whatsapp text-xl' : notificationChannel === 'push' ? 'fas fa-bell text-lg' : 'fas fa-comment-dots text-lg'}></i>
                                            {notificationChannel === 'whatsapp' ? 'Open in WhatsApp' : notificationChannel === 'push' ? 'Send Push Notification' : 'Open in SMS App'}</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
            )}
            
            {/* Direct Bulk Broadcast Modal (No Twilio Required) */}
            {isBulkModalOpen && (() => {
                const recipients = getBulkRecipients();
                const currentRecipient = recipients[bulkQueueIndex] || null;
                return (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-2 sm:p-4 animate-fade-in">
                        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden relative border border-slate-100">
                            <div className="bg-slate-50 px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center text-lg font-extrabold shrink-0">
                                        <i className="fas fa-bullhorn"></i>
                                    </div>
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">Direct Broadcast</h3>
                                        <p className="text-xs text-slate-500 font-medium">Send personalized WhatsApp or SMS</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => { setIsBulkModalOpen(false); setIsQueueActive(false); setBulkQueueIndex(0); }}
                                    className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors shrink-0"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <div className="p-4 sm:p-6 flex flex-col overflow-y-auto flex-grow">
                                {!isQueueActive ? (
                                    <>
                                        <div className="space-y-4 overflow-y-auto pr-1 custom-scrollbar flex-grow my-1">
                                            <div>
                                                <div className="flex justify-between items-center mb-2">
                                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Target Audience</label>
                                                    <span className="text-xs font-extrabold px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                                                        {recipients.length} {recipients.length === 1 ? 'Recipient' : 'Recipients'}
                                                    </span>
                                                </div>
                                                
                                                {/* Audience Mode Switcher: All Customers vs Choose by Name */}
                                                <div className="grid grid-cols-2 gap-2 mb-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkSelectionMode('all')}
                                                        className={`py-2 px-3 rounded-xl font-bold text-xs border transition-all text-center flex items-center justify-center gap-1.5 ${
                                                            bulkSelectionMode === 'all'
                                                                ? 'bg-sky-50 border-sky-400 text-sky-700 shadow-sm'
                                                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <i className="fas fa-users"></i> All Customers
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkSelectionMode('chosen')}
                                                        className={`py-2 px-3 rounded-xl font-bold text-xs border transition-all text-center flex items-center justify-center gap-1.5 ${
                                                            bulkSelectionMode === 'chosen'
                                                                ? 'bg-sky-50 border-sky-400 text-sky-700 shadow-sm'
                                                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <i className="fas fa-user-check"></i> Choose by Name ({selectedUserIds.size})
                                                    </button>
                                                </div>

                                                {bulkSelectionMode === 'all' ? (
                                                    <select
                                                        value={bulkTargetRole}
                                                        onChange={(e) => setBulkTargetRole(e.target.value)}
                                                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold text-xs cursor-pointer"
                                                    >
                                                        <option value="customers">All Registered Customers ({users.filter(u => u.role !== 'admin' && u.role !== 'super_admin' && !u.isAdmin && u.role !== 'driver' && u.mobile).length})</option>
                                                        <option value="driver">All Drivers ({users.filter(u => u.role === 'driver' && u.mobile).length})</option>
                                                        <option value="all">Everyone ({users.filter(u => u.mobile).length})</option>
                                                    </select>
                                                ) : (
                                                    <div className="space-y-2 bg-slate-50 p-3 rounded-2xl border border-slate-200">
                                                        <div className="flex justify-between items-center gap-2">
                                                            <div className="relative flex-grow">
                                                                <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400">
                                                                    <i className="fas fa-search text-[10px]"></i>
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    value={modalUserFilter}
                                                                    onChange={(e) => setModalUserFilter(e.target.value)}
                                                                    placeholder="Search customer name or phone..."
                                                                    className="w-full pl-7 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:border-sky-500"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const allValid = users.filter(u => u.mobile).map(u => u._id);
                                                                    setSelectedUserIds(new Set(allValid));
                                                                }}
                                                                className="text-[10px] font-bold text-sky-600 hover:text-sky-700 bg-white px-2 py-1.5 rounded-lg border border-slate-200 shrink-0"
                                                            >
                                                                Select All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedUserIds(new Set())}
                                                                className="text-[10px] font-bold text-slate-500 hover:text-slate-700 bg-white px-2 py-1.5 rounded-lg border border-slate-200 shrink-0"
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="max-h-36 overflow-y-auto divide-y divide-slate-100 bg-white rounded-xl border border-slate-200/80 p-1">
                                                            {users
                                                                .filter(u => u.mobile)
                                                                .filter(u => {
                                                                    if (!modalUserFilter.trim()) return true;
                                                                    const q = modalUserFilter.toLowerCase();
                                                                    return (u.name || '').toLowerCase().includes(q) || (u.mobile || '').includes(q);
                                                                })
                                                                .map(u => (
                                                                    <label key={u._id} className="flex items-center justify-between p-1.5 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors text-xs">
                                                                        <div className="flex items-center gap-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedUserIds.has(u._id)}
                                                                                onChange={() => handleToggleSelectUser(u._id)}
                                                                                className="w-3.5 h-3.5 text-sky-600 rounded border-slate-300 focus:ring-sky-500 cursor-pointer"
                                                                            />
                                                                            <span className="font-bold text-slate-800">{u.name || 'Anonymous Customer'}</span>
                                                                            <span className="text-[11px] font-mono text-slate-400">{u.mobile}</span>
                                                                        </div>
                                                                        {u.pincode && (
                                                                            <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">
                                                                                {u.pincode}
                                                                            </span>
                                                                        )}
                                                                    </label>
                                                                ))}
                                                        </div>
                                                        <div className="flex justify-between items-center text-[10px] text-slate-400 px-1">
                                                            <span>Click checkboxes to add/remove customers</span>
                                                            <strong className="text-slate-600">{selectedUserIds.size} customers chosen</strong>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Delivery Channel</label>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkChannel('whatsapp')}
                                                        className={`py-2.5 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                            bulkChannel === 'whatsapp'
                                                                ? 'border-emerald-500 bg-emerald-50/70 text-emerald-700 shadow-sm'
                                                                : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                                        }`}
                                                    >
                                                        <i className="fab fa-whatsapp text-emerald-600 text-base"></i> WhatsApp
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkChannel('sms')}
                                                        className={`py-2.5 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                            bulkChannel === 'sms'
                                                                ? 'border-sky-500 bg-sky-50/70 text-sky-700 shadow-sm'
                                                                : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                                        }`}
                                                    >
                                                        <i className="fas fa-comment-dots text-sky-600 text-sm"></i> Native SMS
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkChannel('push')}
                                                        className={`py-2.5 px-2 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition-all ${
                                                            bulkChannel === 'push'
                                                                ? 'border-violet-500 bg-violet-50/70 text-violet-700 shadow-sm'
                                                                : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                                                        }`}
                                                    >
                                                        <i className="fas fa-bell text-violet-600 text-sm"></i> App Push
                                                    </button>
                                                </div>
                                                {bulkChannel === 'push' && (
                                                    <div className="mt-3">
                                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Notification Title</label>
                                                        <input
                                                            type="text"
                                                            value={bulkTitle}
                                                            onChange={(e) => setBulkTitle(e.target.value)}
                                                            placeholder="e.g. Special Announcement from QwickMed"
                                                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-violet-400 rounded-xl outline-none transition-all font-semibold text-sm"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Quick Templates</label>
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {[
                                                        { label: 'Welcome', text: 'Welcome to QwickMed, {name}! 🎉 Get your medicines delivered quickly with flat discounts. Visit: www.qwickmed.com' },
                                                        { label: 'Thanks', text: 'Hi {name}, thank you for choosing QwickMed! We appreciate your trust in us for your healthcare needs.' },
                                                        { label: 'Special Offer', text: 'Hi {name}! 🎁 Special Offer just for you: Get flat 20% off on your next order at QwickMed. Order now!' }
                                                    ].map((tpl, i) => (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            onClick={() => setBulkMessage(tpl.text)}
                                                            className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-bold rounded-lg transition-colors"
                                                        >
                                                            {tpl.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Message Template</label>
                                                <textarea 
                                                    value={bulkMessage}
                                                    onChange={(e) => setBulkMessage(e.target.value)}
                                                    placeholder="Type your message... Use {name} to personalize"
                                                    rows="3"
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-medium resize-none text-sm"
                                                ></textarea>
                                            </div>

                                            {recipients.length > 0 && bulkMessage.trim() && (
                                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Preview for {recipients[0].name || 'First Contact'}:</span>
                                                    <p className="text-xs text-slate-700 font-medium whitespace-pre-wrap">
                                                        {bulkMessage.replace(/{name}/g, recipients[0].name || 'Customer')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="flex gap-2 pt-4 mt-auto flex-shrink-0 border-t border-slate-100">
                                            {bulkChannel !== 'push' && (
                                                <button 
                                                    type="button"
                                                    onClick={handleCopyAllNumbers}
                                                    disabled={recipients.length === 0}
                                                    className="py-3 px-4 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5"
                                                    title="Copy all mobile numbers as comma-separated text"
                                                >
                                                    <i className="fas fa-copy"></i> Copy Numbers
                                                </button>
                                            )}
                                            {bulkChannel === 'push' ? (
                                                <button
                                                    type="button"
                                                    onClick={handleBroadcastPush}
                                                    disabled={!bulkMessage.trim() || recipients.length === 0 || isSendingPush}
                                                    className="flex-grow py-3 text-white rounded-xl font-bold text-sm shadow-md transition-all flex justify-center items-center gap-2 bg-violet-600 hover:bg-violet-700 shadow-violet-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                                >
                                                    {isSendingPush ? (
                                                        <><i className="fas fa-spinner fa-spin"></i> Broadcasting...</>
                                                    ) : (
                                                        <><i className="fas fa-bell text-lg"></i> Broadcast Push ({recipients.length})</>
                                                    )}
                                                </button>
                                            ) : (
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        if (recipients.length === 0) {
                                                            showMessage('No recipients with valid phone numbers found.', 'error');
                                                            return;
                                                        }
                                                        setBulkQueueIndex(0);
                                                        setIsQueueActive(true);
                                                    }}
                                                    disabled={!bulkMessage.trim() || recipients.length === 0}
                                                    className={`flex-grow py-3 text-white rounded-xl font-bold text-sm shadow-md transition-all flex justify-center items-center gap-2 ${
                                                        bulkChannel === 'whatsapp'
                                                            ? 'bg-[#25D366] hover:bg-[#128C7E] shadow-emerald-500/20'
                                                            : 'bg-sky-500 hover:bg-sky-600 shadow-sky-500/20'
                                                    }`}
                                                >
                                                    <i className={bulkChannel === 'whatsapp' ? 'fab fa-whatsapp text-lg' : 'fas fa-paper-plane'}></i>
                                                    Start 1-by-1 Dispatch ({recipients.length})
                                                </button>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-shrink-0 mb-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                    Dispatch Queue: Contact {bulkQueueIndex + 1} of {recipients.length}
                                                </span>
                                                <span className="text-xs font-extrabold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                                                    {Math.round(((bulkQueueIndex) / recipients.length) * 100)}% Done
                                                </span>
                                            </div>
                                            {/* Progress Bar */}
                                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                                <div 
                                                    className="bg-emerald-500 h-full transition-all duration-300"
                                                    style={{ width: `${((bulkQueueIndex) / recipients.length) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>

                                        {currentRecipient ? (
                                            <div className="space-y-4 flex-grow overflow-y-auto pr-1">
                                                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 text-white font-extrabold flex items-center justify-center">
                                                            {(currentRecipient.name || 'U').substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-extrabold text-slate-800 text-base">{currentRecipient.name || 'Customer'}</h4>
                                                            <p className="text-xs font-mono text-slate-500">{currentRecipient.mobile}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Message to be sent:</label>
                                                    <div className="bg-white p-3.5 rounded-xl border border-slate-200 text-xs text-slate-700 font-medium whitespace-pre-wrap">
                                                        {bulkMessage.replace(/{name}/g, currentRecipient.name || 'Customer')}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <i className="fas fa-check-circle text-4xl text-emerald-500 mb-2"></i>
                                                <p className="font-bold text-slate-800">All messages sent!</p>
                                            </div>
                                        )}

                                        <div className="flex gap-2 pt-4 mt-auto flex-shrink-0 border-t border-slate-100">
                                            <button 
                                                type="button"
                                                onClick={() => setIsQueueActive(false)}
                                                className="w-1/4 py-3 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold text-xs transition-all"
                                            >
                                                Exit
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    if (bulkQueueIndex + 1 < recipients.length) {
                                                        setBulkQueueIndex(prev => prev + 1);
                                                    } else {
                                                        setIsQueueActive(false);
                                                    }
                                                }}
                                                className="w-1/4 py-3 border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl font-bold text-xs transition-all"
                                            >
                                                Skip
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={handleSendCurrentInQueue}
                                                className={`w-1/2 py-3 text-white rounded-xl font-bold text-sm shadow-md transition-all flex justify-center items-center gap-2 ${
                                                    bulkChannel === 'whatsapp'
                                                        ? 'bg-[#25D366] hover:bg-[#128C7E] shadow-emerald-500/20'
                                                        : 'bg-sky-500 hover:bg-sky-600 shadow-sky-500/20'
                                                }`}
                                            >
                                                <i className={bulkChannel === 'whatsapp' ? 'fab fa-whatsapp text-lg' : 'fas fa-paper-plane'}></i>
                                                Open & Next
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Edit Customer Modal */}
            {isEditModalOpen && editingUser && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-2 sm:p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden relative border border-slate-100">
                        <div className="bg-slate-50 px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center text-lg font-extrabold shrink-0">
                                    <i className="fas fa-user-edit"></i>
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">Edit Customer Details</h3>
                                    <p className="text-xs text-slate-500 font-medium">Update profile, address, and pincode</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}
                                className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors shrink-0"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Full Name</label>
                                    <input 
                                        type="text" 
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                        placeholder="Customer full name"
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Mobile Number</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-slate-500 font-bold text-sm">+91</span>
                                            </div>
                                            <input 
                                                type="text" 
                                                value={editForm.mobile}
                                                onChange={(e) => setEditForm({...editForm, mobile: e.target.value})}
                                                placeholder="10-digit number"
                                                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Pincode</label>
                                        <input 
                                            type="text" 
                                            value={editForm.pincode}
                                            onChange={(e) => setEditForm({...editForm, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                                            placeholder="e.g. 695125"
                                            maxLength="6"
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Delivery Address</label>
                                    <textarea 
                                        value={editForm.address}
                                        onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                                        placeholder="Complete address, landmark, house/flat no..."
                                        rows="3"
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold resize-none"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-6 mt-6 border-t border-slate-100">
                                <button 
                                    onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}
                                    className="w-1/3 py-3 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleSaveUserDetails}
                                    disabled={isProcessing || !editForm.name.trim()}
                                    className="w-2/3 py-3 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-xl font-bold shadow-md shadow-sky-500/20 transition-all flex justify-center items-center gap-2"
                                >
                                    {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Changes</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
